import { useState } from "react";
import { createPortal } from "react-dom";
import { Sparkle, X, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Billing, BillingEmail } from "../lib/api";
import { CaptureMoment } from "../lib/captureMoment";

/**
 * PostCaptureNudge — the conversion-moment modal.
 *
 * Rendered via React Portal to `document.body` so the `fixed inset-0`
 * backdrop escapes any ancestor `transform` / `animation` containing
 * block (e.g. the .page-fade wrapper on Sessions).
 *
 * Fires ONCE, on Sessions page mount, IF:
 *   - the user just exited Camera with >0 captures (CaptureMoment.readPending())
 *   - the user is NOT already paid
 *   - the user did NOT dismiss in the last 24h
 */
export function PostCaptureNudge({ payload, onClose, onUpgraded }) {
  const [email, setEmail] = useState(BillingEmail.get());
  const [submitting, setSubmitting] = useState(false);

  const dismiss = () => {
    CaptureMoment.markDismissed();
    onClose();
  };

  const submit = async () => {
    const e = (email || "").trim().toLowerCase();
    if (!e || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) {
      toast.error("ENTER A VALID EMAIL");
      return;
    }
    BillingEmail.set(e);
    setSubmitting(true);
    try {
      const r = await Billing.createCheckoutSession({ email: e });
      CaptureMoment.clearPending();
      window.location.href = r.url;
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "CHECKOUT FAILED";
      toast.error(String(msg).toUpperCase().slice(0, 120));
      setSubmitting(false);
    }
  };

  const count = payload?.count || 0;
  const session = payload?.sessionName;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-4"
      data-testid="post-capture-nudge"
      onClick={dismiss}
    >
      <div
        className="bg-white border-2 border-black w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: "8px 8px 0 0 #FF2A2A" }}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b-2 border-black flex items-start justify-between gap-3">
          <div>
            <p className="label-mono text-neutral-500" data-testid="post-capture-eyebrow">
              {count} {count === 1 ? "FRAME" : "FRAMES"} CAPTURED
              {session ? ` · ${session.toUpperCase()}` : ""}
            </p>
            <p className="font-display font-black tracking-tighter leading-[0.95] text-3xl mt-1">
              LOCK THESE
              <br />
              IN FOREVER.
            </p>
          </div>
          <button
            data-testid="post-capture-nudge-close"
            onClick={dismiss}
            className="btn-flat-outline px-2 py-1"
            aria-label="Dismiss"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        {/* Value */}
        <div className="px-5 py-4 space-y-2">
          <p className="font-mono text-sm text-neutral-700">
            Your first sync unlocks cloud backup, multi-device, and the
            AIImageSuite.pro pipeline.
          </p>
          <p className="label-mono text-neutral-500">
            $12/MONTH · CANCEL ANYTIME
          </p>
        </div>

        {/* Action */}
        <div className="px-5 py-4 border-t-2 border-black space-y-2">
          <input
            type="email"
            data-testid="post-capture-email-input"
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
            placeholder="you@studio.com"
            className="w-full border-2 border-black px-3 py-2 font-mono text-sm"
            autoFocus
          />
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="post-capture-upgrade-btn"
            className="w-full btn-flat px-5 py-3 inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {submitting ? (
              "REDIRECTING…"
            ) : (
              <>
                <Sparkle size={14} weight="bold" /> ENABLE CLOUD SYNC
                <ArrowRight size={16} weight="bold" />
              </>
            )}
          </button>
          <button
            onClick={dismiss}
            data-testid="post-capture-later"
            className="w-full label-mono text-neutral-500 hover:text-black py-1"
          >
            Not now
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
